import { useState, type FormEvent } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useTranslation, useLocale } from '@wovendays/i18n';
import { apiClient } from '@/lib/api-client';
import { useAuth } from '@/contexts/auth';
import { useLocaleSync } from '@/hooks/useLocaleSync';
import { Tile } from '@/components/ui/tile';
import { Input } from '@/components/ui/input';
import { Button } from '@/components/ui/button';
import { Alert, AlertDescription } from '@/components/ui/alert';
import { Select } from '@/components/ui/select';
import { ArrowLeft } from 'lucide-react';
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from '@/components/ui/dialog';

export function ProfilePage() {
  const { user } = useAuth();
  const { t } = useTranslation('profile');
  const { locale, setLocale, supportedLocales, localeLabels } = useLocale();
  const { persistLocale } = useLocaleSync(user);
  const queryClient = useQueryClient();
  const navigate = useNavigate();
  const [name, setName] = useState(user?.name ?? '');
  const [saved, setSaved] = useState(false);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [deleteConfirmText, setDeleteConfirmText] = useState('');
  const [deleteError, setDeleteError] = useState<string | null>(null);
  const [ownedFamilies, setOwnedFamilies] = useState<{ id: string; name: string }[]>([]);

  const mutation = useMutation({
    mutationFn: (newName: string) =>
      apiClient.put<{ user: { id: string; email: string; name: string; avatarUrl: string | null } }>('/api/auth/profile', { name: newName }),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['auth', 'session'] });
      queryClient.invalidateQueries({ queryKey: ['families'] });
      setSaved(true);
      setTimeout(() => setSaved(false), 3000);
    },
  });

  const deleteAccountMutation = useMutation({
    mutationFn: () => apiClient.delete<{ success?: boolean; error?: string; ownedFamilies?: { id: string; name: string }[] }>('/api/auth/account'),
    onSuccess: () => {
      queryClient.clear();
      navigate('/login');
    },
    onError: (err: any) => {
      const data = err?.data;
      if (data?.ownedFamilies) {
        setOwnedFamilies(data.ownedFamilies);
        setDeleteError('You must transfer or delete all families you own first.');
      } else {
        setDeleteError(err instanceof Error ? err.message : 'Failed to delete account');
      }
    },
  });

  const handleSubmit = (e: FormEvent) => {
    e.preventDefault();
    if (!name.trim()) return;
    mutation.mutate(name.trim());
  };

  return (
    <div>
      <div className="mb-4">
        <Link to="/" className="inline-flex items-center gap-1 text-sm text-muted-foreground hover:text-foreground transition-colors">
          <ArrowLeft className="h-3.5 w-3.5" />
          {t('common:back')}
        </Link>
      </div>

      <Tile variant="white" className="p-6 max-w-md">
        <h2 className="font-serif text-xl text-foreground mb-4">{t('title')}</h2>

        <form onSubmit={handleSubmit}>
          <div className="mb-4">
            <label htmlFor="profile-email" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              {t('email')}
            </label>
            <p id="profile-email" className="text-sm text-foreground">{user?.email}</p>
          </div>

          <div className="mb-4">
            <label htmlFor="profile-name" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              {t('displayName')}
            </label>
            <Input
              id="profile-name"
              type="text"
              value={name}
              onChange={(e) => { setName(e.target.value); setSaved(false); }}
              maxLength={100}
              aria-label={t('displayName')}
            />
          </div>

          <div className="mb-4">
            <label htmlFor="profile-language" className="block font-mono text-xs uppercase tracking-wide text-muted-foreground mb-1.5">
              {t('language')}
            </label>
            <Select
              id="profile-language"
              value={locale}
              onChange={(e) => { const loc = e.target.value as any; setLocale(loc); persistLocale(loc); }}
              aria-label={t('language')}
            >
              {supportedLocales.map((loc) => (
                <option key={loc} value={loc}>{localeLabels[loc]}</option>
              ))}
            </Select>
          </div>

          {mutation.isError && (
            <Alert variant="destructive" className="mb-4">
              <AlertDescription>
                {mutation.error instanceof Error ? mutation.error.message : t('updateFailed')}
              </AlertDescription>
            </Alert>
          )}

          {saved && (
            <Alert className="mb-4">
              <AlertDescription>{t('updateSuccess')}</AlertDescription>
            </Alert>
          )}

          <div className="flex gap-2">
            <Button type="submit" disabled={mutation.isPending || !name.trim()}>
              {mutation.isPending ? t('common:saving') : t('common:save')}
            </Button>
            <Link to="/">
              <Button type="button" variant="ghost">{t('common:cancel')}</Button>
            </Link>
          </div>
        </form>
      </Tile>

      {/* Danger Zone */}
      <Tile variant="white" className="p-6 max-w-md mt-4 border border-destructive/20">
        <h3 className="font-mono text-xs uppercase tracking-wide text-destructive mb-2">Danger Zone</h3>
        <p className="text-sm text-muted-foreground mb-3">
          Permanently delete your account. All your comments, reactions, and loves will be removed.
          Photos you uploaded will be reassigned to the family owner.
        </p>
        <button
          onClick={() => { setDeleteConfirmText(''); setDeleteError(null); setOwnedFamilies([]); setShowDeleteDialog(true); }}
          className="font-mono text-xs uppercase tracking-wide text-destructive hover:text-destructive/80 transition-colors"
        >
          Delete Account
        </button>

        <Dialog open={showDeleteDialog} onOpenChange={setShowDeleteDialog}>
          <DialogContent>
            <DialogHeader>
              <DialogTitle>Delete your account?</DialogTitle>
              <DialogDescription>
                This will permanently delete your account. All your comments, reactions, and loves will be removed.
                Photos you uploaded will be reassigned to the family owner. This cannot be undone.
              </DialogDescription>
            </DialogHeader>
            <div className="py-4">
              {deleteError && (
                <Alert variant="destructive" className="mb-4">
                  <AlertDescription>
                    {deleteError}
                    {ownedFamilies.length > 0 && (
                      <ul className="mt-2 list-disc list-inside">
                        {ownedFamilies.map(f => (
                          <li key={f.id}>
                            <Link to={`/families/${f.id}/settings`} className="underline" onClick={() => setShowDeleteDialog(false)}>
                              {f.name}
                            </Link>
                          </li>
                        ))}
                      </ul>
                    )}
                  </AlertDescription>
                </Alert>
              )}
              <label htmlFor="delete-account-confirm" className="block text-sm text-foreground mb-2">
                Type <span className="font-semibold">DELETE</span> to confirm
              </label>
              <Input
                id="delete-account-confirm"
                type="text"
                value={deleteConfirmText}
                onChange={(e) => setDeleteConfirmText(e.target.value)}
                placeholder="DELETE"
                autoFocus
              />
            </div>
            <DialogFooter>
              <Button variant="ghost" onClick={() => setShowDeleteDialog(false)}>
                {t('common:cancel')}
              </Button>
              <Button
                variant="destructive"
                disabled={deleteConfirmText !== 'DELETE' || deleteAccountMutation.isPending}
                onClick={() => deleteAccountMutation.mutate()}
              >
                {deleteAccountMutation.isPending ? t('common:deleting') : 'Delete Account'}
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
      </Tile>
    </div>
  );
}
